import React, { ChangeEvent, FC, useEffect, useState, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import {
  Combobox,
  Option,
  TextInput,
  Button,
  ButtonColor,
  ButtonSize,
  Table,
  TableRow,
  TextArea,
  Modal,
  ModalType,
} from '@code/innovance-react-ui-kit';

import {
  ReferenceDataType,
  PtsSearchResponseData,
  DocumentPageUploadData,
} from 'src/front/api/generated';
import { ProblemTrackingSystemApi } from 'src/front/api/apiInstance';

import usePRM, { useConditionalPRM } from 'src/front/common/hooks/usePRM';
import useYupResolver from 'src/front/common/hooks/useYupResolver';

import { useUserInfoState, useUserNameState } from 'src/front/contexts/SessionContext';

import {
  PTSViewRecordsSchema,
  PTSViewRecordsResultSchema,
} from 'src/front/common/utils/validationSchemas';
import { formatDate } from 'src/front/common/utils/dateTimeUtils';
import {
  formatNumeric,
  formatAlphaNumericCyrillicSpecialCharactersWithoutQuotes,
} from 'src/front/common/utils/formUtils';
import { TABLE_WIDTHS } from 'src/front/constant';

import {
  FormElement,
  FormRow,
  Title,
  Separator,
  ButtonRow,
  InfoRow,
  InfoLabel,
  TableWrapper,
  TextAreaContainer,
  ButtonsRow,
} from 'src/front/components/FormStyles';

import UploadedDocumentsTable from 'src/front/pages/applications/problem-tracking-system/components/UploadedDocumentsTable';
import SelectedRecordData from 'src/front/pages/applications/problem-tracking-system/components/SelectedRecordData';
import RecordHistoryTable from 'src/front/pages/applications/problem-tracking-system/components/RecordHistoryTable';
import Datepicker from 'src/front/components//Datepicker';
import { useProblemTrackingSystemState } from 'src/front/contexts/ProblemTrackingSystemContext';

const recordTableHeaders = [
  'problemTrackingSystem_applicationId_label',
  'problemTrackingSystem_date_label',
  'problemTrackingSystem_time_label',
  'general_components_userCode_label',
  'problemTrackingSystem_branch_label',
  'problemTrackingSystem_vip_label',
  'general_components_fullName_text',
  'problemTrackingSystem_level0_label',
  'problemTrackingSystem_level1_label',
  'problemTrackingSystem_level2_label',
  'problemTrackingSystem_level3_label',
  'general_components_productType_label',
  'general_status_label',
  'problemTrackingSystem_classificationCode_label',
  'general_components_channelCode_label',
  'general_components_deadline_label',
  'problemTrackingSystem_pool_label',
  'problemTrackingSystem_typeOfClosure_label',
  'problemTrackingSystem_sendSms_label',
];

interface FormValues {
  startDate: Date | null;
  endDate: Date | null;
  statusType: Option | null;
  productType: Option | null;
  channelType: Option | null;
  poolType: Option | null;
  classificationCode: string;
  applicationId: string;
  levelZero: Option | null;
  levelOne: Option | null;
  levelTwo: Option | null;
  levelThree: Option | null;
}

const defaultValues: FormValues = {
  startDate: null,
  endDate: null,
  statusType: null,
  productType: null,
  channelType: null,
  poolType: null,
  classificationCode: '',
  applicationId: '',
  levelZero: null,
  levelOne: null,
  levelTwo: null,
  levelThree: null,
};

const ViewRecords: FC = () => {
  const { t } = useTranslation();
  const { processDate } = useUserInfoState();
  const userName = useUserNameState();
  const { customerSearchValues } = useProblemTrackingSystemState();
  const resolver = useYupResolver(PTSViewRecordsSchema);
  const {
    formState: { errors },
    control,
    getValues,
    handleSubmit,
    setValue,
    watch,
    reset,
  } = useForm({
    resolver,
    defaultValues: {
      ...defaultValues,
      startDate: processDate || null,
      endDate: processDate || null,
    },
  });

  const [result, setResult] = useState<string>('');
  const [sendType, setSendType] = useState<Option | null>(null);

  const [page, setPage] = useState<number>(1);
  const [isUpdate, setIsUpdate] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);
  const [resultValidationError, setResultValidationError] = useState<string>();
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [isLoadingTable, setLoadingTable] = useState<boolean>(false);
  const [recordsData, setRecordsData] = useState<PtsSearchResponseData[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<TableRow | undefined>();
  const [isUpdateDisabled, setIsUpdateDisabled] = useState<boolean>(false);
  const [isResultDisabled, setIsResultDisabled] = useState<boolean>(true);
  const [recordsTableItems, setRecordsTableItems] = useState<TableRow[]>([]);
  const [selectedRecordData, setSelectedRecordData] = useState<PtsSearchResponseData>();
  const [isApplicationCompleted, setIsApplicationCompleted] = useState<boolean>(false);
  const [applicationSubmitError, setApplicationSubmitError] = useState<string>();
  const [newUploadedDocuments, setNewUploadedDocuments] = useState<DocumentPageUploadData[]>([]);

  const levelZeroPRM = usePRM(ReferenceDataType.PTSLEVELZERO);
  const levelOnePRM = usePRM(ReferenceDataType.PTSLEVELONE);
  const levelTwoPRM = useConditionalPRM(
    ReferenceDataType.PTSLEVELTWO,
    watch('levelOne')?.value || ''
  );
  const [levelThreePRM, setLevelThreePRM] = useState<Option[]>([]);
  const poolTypePRM = usePRM(ReferenceDataType.PTSPOOL);
  const statusTypePRM = usePRM(ReferenceDataType.PTSSTATUS);
  const channelCodePRM = usePRM(ReferenceDataType.PTSCHANNELCODE);
  const productTypePRM = usePRM(ReferenceDataType.PTSPRODUCTTYPE);

  useEffect(() => {
    setIsResultDisabled(!isUpdate);
  }, [isUpdate]);

  useEffect(() => {
    setSendType({
      label: selectedRecordData?.pool.value || '',
      value: selectedRecordData?.pool.code || '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRecord]);

  const loadRecords = useCallback(
    async (pageNumber = 1, forceRefresh = false): Promise<void> => {
      setLoadingTable(true);
      setShowResults(false);

      const formData = getValues();
      const {
        customerId,
        customerNumber,
        customerName,
        customerSecondName,
        customerSurname,
        birthDate,
      } = customerSearchValues;

      const { data } = await ProblemTrackingSystemApi.searchPtsRecords({
        customerId,
        customerName,
        customerSecondName,
        customerSurname,
        customerNumber,
        birthDate: formatDate(birthDate),
        startDate: formatDate(formData.startDate),
        endDate: formatDate(formData.endDate),
        rowCountPerPage: 5,
        pageNumber: pageNumber || page,
        refresh: forceRefresh,
        status: formData?.statusType?.value,
        productType: formData?.productType?.value,
        channelCode: formData?.channelType?.value,
        pool: formData?.poolType?.value,
        classificationCode: formData?.classificationCode,
        applicationId: formData?.applicationId,
        levelZero: formData?.levelZero?.value,
        levelOne: formData?.levelOne?.value,
        levelTwo: formData?.levelTwo?.value,
        levelThree: formData?.levelThree?.value,
      });

      if (data.result === 'OK') {
        setPage(pageNumber || 1);
        setHasNextPage(data.hasNextPage);
        setRecordsData(data.recordList);
        setRecordsTableItems(
          data.recordList.map((record) => ({
            id: record.problemId || '',
            columns: [
              record.applicationId || '',
              record.date || '',
              record.time || '',
              record.userCode || '',
              record.branch || '',
              record.vip ? t('general_yes_text') : t('general_no_text'),
              record.fullName || '',
              record.levelZero.value,
              record.levelOne.value,
              record.levelTwo.value,
              record.levelThree.value,
              record.productType.value,
              record.status.value,
              record.classificationCode || '',
              record.channel.value,
              record.deadline || '',
              record.pool.value,
              record.typeOfClosure.value,
              record.sendSms ? t('general_yes_text') : t('general_no_text'),
            ],
          }))
        );
      }

      setLoadingTable(false);
      setShowResults(true);
    },
    [customerSearchValues, getValues, page, t]
  );

  const requestLevelThreeCombo = async (): Promise<void> => {
    const levelValues = getValues(['levelOne', 'levelTwo']);

    const { data } = await ProblemTrackingSystemApi.getPtsLevelThreeCombo({
      levelOne: levelValues?.levelOne?.value || '',
      levelTwo: levelValues?.levelTwo?.value || '',
    });

    setLevelThreePRM(
      data.parameterDataList?.map((parameter) => ({
        value: parameter.code,
        label: parameter.value,
      })) || []
    );
  };

  const handleInputChange = (fieldName: string) => (text: string) => {
    setValue(fieldName, text);
  };

  const handleResultAreaChange = ({ target }: ChangeEvent<HTMLTextAreaElement>): void => {
    const { value } = target;

    setResult(formatAlphaNumericCyrillicSpecialCharactersWithoutQuotes(value));
  };

  const onComboChange = (fieldName: string) => (value: Option | null): void => {
    setValue(fieldName, value);

    switch (fieldName) {
      case 'levelOne':
        // Level 1 Changed / Reset
        setValue('levelTwo', null); // Reset Level 2
        setValue('levelThree', null); // Reset Level 3
        setLevelThreePRM([]); // Reset Level 3 Data
        break;
      case 'levelTwo':
        // Level 2 Changed / Reset
        requestLevelThreeCombo();
        setValue('levelThree', null); // Reset Level 3
        break;
      default:
        break;
    }
  };

  const lockRecord = useCallback(async (): Promise<void> => {
    await ProblemTrackingSystemApi.lockPtsRecord({
      problemId: selectedRecord?.id || '',
    });
  }, [selectedRecord]);

  const handleUpdateLock = async (): Promise<void> => {
    const { data } = await ProblemTrackingSystemApi.checkPtsRecordStatus({
      problemId: selectedRecord?.id || '',
    });

    if (data.result === 'OK') {
      if (data.isLocked) {
        if (data.userCode === userName) {
          setIsUpdate(true);
        } else {
          setIsUpdateDisabled(true);
        }
      } else {
        await lockRecord();

        setIsUpdate(true);
      }
    }
  };

  const handleUnlock = useCallback(async (): Promise<void> => {
    setIsResultDisabled(true);
    setResult('');

    const { data } = await ProblemTrackingSystemApi.unlockPtsRecord({
      problemId: selectedRecord?.id || '',
    });

    if (data.result === 'OK') {
      setIsUpdate(false);
    }
  }, [selectedRecord]);

  const handleSelectRecord = useCallback(
    async (record: TableRow | undefined): Promise<void> => {
      setSelectedRecord(record);
      setSelectedRecordData(recordsData.find((rec) => rec.problemId === record?.id));

      handleUnlock();
    },
    [handleUnlock, recordsData]
  );

  const handleSave = async (): Promise<void> => {
    const isValidResult = await PTSViewRecordsResultSchema.validate({ result }).catch(
      ({ message }) => {
        setResultValidationError(message);
      }
    );

    if (isValidResult && selectedRecordData) {
      setResultValidationError(undefined);

      const {
        problemId,
        levelZero,
        levelOne,
        levelTwo,
        levelThree,
        productType,
        channel,
        deadline,
        sendSms,
        vip,
        classificationCode,
        status,
        typeOfClosure,
        problemText,
      } = selectedRecordData;

      const { data } = await ProblemTrackingSystemApi.updatePtsRecord({
        problemId,
        levelZero: levelZero.code,
        levelOne: levelOne.code,
        levelTwo: levelTwo.code,
        levelThree: levelThree.code,
        productType: productType.code,
        channelCode: channel.code,
        deadline,
        isSmsChecked: sendSms,
        isVipChecked: vip,
        classificationCode,
        sendType: sendType?.value,
        status: status.code,
        closureType: typeOfClosure.code,
        detailAboutRecord: problemText,
        comments: result,
        documentList: newUploadedDocuments,
      });

      if (data.result === 'OK') {
        setIsApplicationCompleted(true);
      }
    }
  };

  const handleApplicationSuccessClick = (): void => {
    // Reset All
    reset();
    setIsUpdate(false);
    setShowResults(false);
    setSelectedRecord(undefined);
    setIsApplicationCompleted(false);
    setResult('');
    setSendType(null);
  };

  const handleApplicationErrorClick = (): void => {
    setApplicationSubmitError(undefined); // Close modal
  };

  const onSubmit = async (): Promise<void> => {
    await loadRecords(1, true);
  };

  return (
    <>
      <Title>
        <Trans i18nKey="problemTrackingSystem_customerRecords_text" />
      </Title>
      <Separator />
      <form onSubmit={handleSubmit(onSubmit)}>
        <InfoRow>
          <InfoLabel>
            <Trans i18nKey="general_components_period_text" />
          </InfoLabel>
        </InfoRow>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="startDate"
              render={({ onChange, value }) => (
                <Datepicker
                  errorMessage={t(errors?.startDate?.message as string)}
                  inputLabel={`${t('executedOperations_startDate_label')} *`}
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </FormElement>
          <FormElement>
            <Controller
              control={control}
              name="endDate"
              render={({ onChange, value }) => (
                <Datepicker
                  errorMessage={t(errors?.endDate?.message as string)}
                  inputLabel={`${t('executedOperations_endDate_label')} *`}
                  onChange={onChange}
                  value={value}
                />
              )}
            />
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="statusType"
              render={({ value }) => (
                <Combobox
                  errorMessage={t(errors.statusType?.message as string)}
                  items={statusTypePRM}
                  label={t('general_status_label')}
                  name="statusType"
                  onChange={onComboChange('statusType')}
                  value={value}
                />
              )}
            />
          </FormElement>
          <FormElement>
            <Controller
              control={control}
              name="productType"
              render={({ value }) => (
                <Combobox
                  errorMessage={t(errors.productType?.message as string)}
                  items={productTypePRM}
                  label={t('general_components_productType_label')}
                  name="productType"
                  onChange={onComboChange('productType')}
                  value={value}
                />
              )}
            />
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="channelType"
              render={({ value }) => (
                <Combobox
                  errorMessage={t(errors.channelType?.message as string)}
                  items={channelCodePRM}
                  label={t('general_components_channelCode_label')}
                  name="channelType"
                  onChange={onComboChange('channelType')}
                  value={value}
                />
              )}
            />
          </FormElement>
          <FormElement>
            <Controller
              control={control}
              name="poolType"
              render={({ value }) => (
                <Combobox
                  errorMessage={t(errors.poolType?.message as string)}
                  items={poolTypePRM}
                  label={t('problemTrackingSystem_pool_label')}
                  name="poolType"
                  onChange={onComboChange('poolType')}
                  value={value}
                />
              )}
            />
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="classificationCode"
              render={({ value }) => (
                <TextInput
                  errorMessage={t(errors.classificationCode?.message as string)}
                  formatter={formatAlphaNumericCyrillicSpecialCharactersWithoutQuotes}
                  label={t('problemTrackingSystem_classificationCode_label')}
                  maxLength={50}
                  name="classificationCode"
                  onChange={handleInputChange('classificationCode')}
                  value={value}
                />
              )}
            />
          </FormElement>
          <FormElement>
            <Controller
              control={control}
              name="applicationId"
              render={({ value }) => (
                <TextInput
                  errorMessage={t(errors.applicationId?.message as string)}
                  formatter={formatNumeric}
                  label={t('problemTrackingSystem_applicationId_label')}
                  maxLength={6}
                  name="applicationId"
                  onChange={handleInputChange('applicationId')}
                  value={value}
                />
              )}
            />
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="levelZero"
              render={({ value }) => (
                <Combobox
                  errorMessage={t(errors.levelZero?.message as string)}
                  items={levelZeroPRM}
                  label={t('problemTrackingSystem_level0_label')}
                  name="levelZero"
                  onChange={onComboChange('levelZero')}
                  value={value}
                />
              )}
            />
          </FormElement>
          <FormElement>
            <Controller
              control={control}
              name="levelOne"
              render={({ value }) => (
                <Combobox
                  errorMessage={t(errors.levelOne?.message as string)}
                  items={levelOnePRM}
                  label={t('problemTrackingSystem_level1_label')}
                  name="levelOne"
                  onChange={onComboChange('levelOne')}
                  value={value}
                />
              )}
            />
          </FormElement>
        </FormRow>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="levelTwo"
              render={({ value }) => (
                <Combobox
                  disabled={!levelTwoPRM.length}
                  errorMessage={t(errors.levelTwo?.message as string)}
                  items={levelTwoPRM}
                  label={t('problemTrackingSystem_level2_label')}
                  name="levelTwo"
                  onChange={onComboChange('levelTwo')}
                  value={value}
                />
              )}
            />
          </FormElement>
          <FormElement>
            <Controller
              control={control}
              name="levelThree"
              render={({ value }) => (
                <Combobox
                  disabled={!levelThreePRM.length}
                  errorMessage={t(errors.levelThree?.message as string)}
                  items={levelThreePRM}
                  label={t('problemTrackingSystem_level3_label')}
                  name="levelThree"
                  onChange={onComboChange('levelThree')}
                  value={value}
                />
              )}
            />
          </FormElement>
        </FormRow>
        <Separator />
        <ButtonRow>
          <Button
            color={ButtonColor.Primary}
            size={ButtonSize.Medium}
            text={t('general_components_search_button')}
            type="submit"
          />
        </ButtonRow>
      </form>
      {showResults ? (
        <>
          <Separator />
          <InfoRow>
            <InfoLabel>
              <Trans i18nKey="problemTrackingSystem_recordList_text" />
            </InfoLabel>
          </InfoRow>
          <TableWrapper>
            <Table
              emptyTableText={t('general_components_notFound_errorMessage')}
              hasNextPage={hasNextPage}
              headers={recordTableHeaders.map((head) => t(head))}
              items={recordsTableItems}
              loading={isLoadingTable}
              loadPageItems={loadRecords}
              onSelect={handleSelectRecord}
              pageNumber={page}
              selectedItem={selectedRecord}
              tableWidth={TABLE_WIDTHS.DESKTOP_3X}
              widths={
                // eslint-disable-next-line
                [
                  '5',
                  '5',
                  '5',
                  '5',
                  '5',
                  '5',
                  '5',
                  '5',
                  '5',
                  '5',
                  '5',
                  '5',
                  '10',
                  '5',
                  '5',
                  '5',
                  '5',
                  '5',
                  '5',
                ]
              }
              clearItemOnPageChange
              withRadio
            />
          </TableWrapper>
        </>
      ) : null}
      {selectedRecord && selectedRecordData ? (
        <>
          <SelectedRecordData
            selectedRecordData={selectedRecordData}
            sendType={sendType}
            setIsUpdateDisabled={setIsUpdateDisabled}
            setSendType={setSendType}
          />
          <UploadedDocumentsTable
            customerId={customerSearchValues.customerId}
            inUpdateMode={isUpdate}
            newUploadedDocuments={newUploadedDocuments}
            problemId={selectedRecord.id}
            setNewUploadedDocuments={setNewUploadedDocuments}
          />
          <RecordHistoryTable problemId={selectedRecord.id} />

          <FormRow>
            <TextAreaContainer>
              <TextArea
                disabled={isResultDisabled}
                errorMessage={t(resultValidationError as string)}
                label={`${
                  isUpdate
                    ? t('general_components_comment_label')
                    : t('general_components_result_label')
                } *`}
                maxLength={2000}
                onChange={handleResultAreaChange}
                value={result}
              />
            </TextAreaContainer>
          </FormRow>
          <Separator />
          <ButtonsRow>
            {!isUpdate ? (
              <>
                <Button
                  color={ButtonColor.Primary}
                  disabled={isUpdateDisabled}
                  onClick={handleUpdateLock}
                  size={ButtonSize.Medium}
                  text={t('general_components_updateLock_button')}
                  type="button"
                />
              </>
            ) : (
              <>
                <Button
                  color={ButtonColor.Secondary}
                  onClick={handleUnlock}
                  size={ButtonSize.Medium}
                  text={t('general_components_unlock_button')}
                  type="button"
                />
                <Button
                  color={ButtonColor.Primary}
                  onClick={handleSave}
                  size={ButtonSize.Medium}
                  text={t('general_save_button')}
                  type="button"
                />
              </>
            )}
          </ButtonsRow>
        </>
      ) : null}
      {isApplicationCompleted ? (
        <Modal
          description={t('problemTrackingSystem_successMessageUpdate_text')}
          onCancel={handleApplicationSuccessClick}
          submitText={t('general_components_ok_button')}
          title={t('general_wellDone_text')}
          type={ModalType.Success}
        />
      ) : null}
      {applicationSubmitError ? (
        <Modal
          description={applicationSubmitError}
          onCancel={handleApplicationErrorClick}
          submitText={t('general_components_ok_button')}
          title={t('general_warning_text')}
          type={ModalType.Error}
        />
      ) : null}
    </>
  );
};

export default ViewRecords;
